import { useEffect, useMemo, useState } from "react";
import { EntityDetailPanel, Page, QuickStatsWidget } from "@iq/models";
import { FaRegBuilding } from "react-icons/fa";
import { FaRegNoteSticky } from "react-icons/fa6";
import { FiBriefcase, FiCalendar, FiDollarSign, FiMail, FiPhone, FiZap } from "react-icons/fi";
import { LuHandshake } from "react-icons/lu";
import { MdOutlineModeEdit, MdOutlineTouchApp } from "react-icons/md";
import TabView from "../../../CRM/components/TabView/TabView";
import { getPaymentClientById } from "../../../services/paymentApi";
import Payment from "./tabs/Payment";
import "./ClientDetail.css";

const CLIENT_TABS = ["Overview", "Payments", "Invoices", "Quotes", "Notes"];

function ClientDetail({ clientId, initialClient = null, onBack, setPageHeader }) {
	const [client, setClient] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [activeTab, setActiveTab] = useState("Overview");

	useEffect(() => {
		setPageHeader?.({
			title: client?.name || initialClient?.name || "Client",
			back: true,
			onBack,
			children: null,
		});
	}, [setPageHeader, client, initialClient, onBack]);

	useEffect(() => {
		if (!clientId) {
			setClient(null);
			return;
		}

		let cancelled = false;
		setLoading(true);
		setError("");

		getPaymentClientById(clientId)
			.then((data) => {
				if (cancelled) {
					return;
				}
				setClient(data || null);
				if (!data) {
					setError("Client not found");
				}
			})
			.catch((err) => {
				if (cancelled) {
					return;
				}
				setClient(null);
				setError(err?.message || "Failed to load client");
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [clientId]);

	const tabViews = useMemo(() => ({
		Overview: <div className="payment-client-placeholder">Client overview will appear here.</div>,
		Payments: <Payment clientId={clientId} client={client} />,
		Invoices: <div className="payment-client-placeholder">Invoice history will appear here.</div>,
		Quotes: <div className="payment-client-placeholder">Quote details will appear here.</div>,
		Notes: <div className="payment-client-placeholder">Notes will appear here.</div>,
	}), [clientId, client]);

	const detailPanelFieldIcons = {
		phone: <FiPhone />,
		email: <FiMail />,
		title: <FiBriefcase />,
		dealType: <LuHandshake />,
		dealValue: <FiDollarSign />,
		lastTouch: <FaRegBuilding />,
		nextStep: <FiZap />,
	};

	const detailPanelActions = [
		{ label: "Compose", icon: <FiMail /> },
		{ label: "Log Call", icon: <FiPhone /> },
		{ label: "Add Note", icon: <FaRegNoteSticky /> },
		{ label: "Create Deal", icon: <FiBriefcase /> },
		{ label: "Meet", icon: <FiCalendar /> },
		{ label: "Follow-Up", icon: <MdOutlineTouchApp /> },
	];

	return (
		<Page>
			<div className="payment-client-detail-page">
				<div className="payment-client-detail-layout">
					<aside className="payment-client-detail-left">
						<EntityDetailPanel
							displayName={client?.name || "N/A"}
							displayCompany={client?.company || ""}
							displayOwner={"Payment Team"}
							editIcon={<MdOutlineModeEdit />}
							fieldIcons={detailPanelFieldIcons}
							displayPhone={client?.phone_number || "N/A"}
							displayEmail={client?.email || "N/A"}
							displayTitle={"Client"}
							displayDealType={"Subscription"}
							displayDealValueFormatted={client?.amount || "N/A"}
							displayDealStage={client?.status || "N/A"}
							displayLastTouch={"N/A"}
							displayNextStep={"N/A"}
							actions={detailPanelActions}
							onActionClick={() => {}}
						/>

						<QuickStatsWidget
							variant="payment"
							stats={{
								"Invoices Sent": 0,
								"Quotes Sent": 0,
								MRR: 0,
								"Total Revenue": 0,
							}}
						/>
					</aside>

					<div className="payment-client-detail-main">
						{loading && <div className="payment-client-tab-content">Loading client details...</div>}
						{error && !loading && <div className="payment-client-tab-content">{error}</div>}
						{!loading && !error && (
							<TabView
								tabs={CLIENT_TABS}
								views={tabViews}
								activeTab={activeTab}
								onTabChange={setActiveTab}
								ariaLabel="Payment client tabs"
								className="payment-client-tabs"
								contentClassName="payment-client-tab-content"
							/>
						)}
					</div>
				</div>
			</div>
		</Page>
	);
}

export default ClientDetail;
